import { UserOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Form,
  Input,
  Space,
  Spin,
} from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { usernameChange } from '../../reducers/user/reducer';
import {
  ADMIN_USERNAME_MAX_LENGTH,
  ADMIN_USERNAME_MIN_LENGTH,
  ADMIN_USERNAME_REGEX,
} from '../../reducers/user/username-policy';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';

interface IProfileForm {
  username: string;
}

function ProfileForm(): React.ReactElement {
  const dispatch = useAppDispatch();
  const userEmail = useSelector((s: RootState) => s.User.email);
  const userUuid = useSelector((s: RootState) => s.User.uuid);
  const userUsername = useSelector((s: RootState) => s.User.username);
  const userInProcess = useSelector((s: RootState) => s.User.inProcess);

  const [form] = Form.useForm<IProfileForm>();

  useEffect(() => {
    form.setFieldsValue({ username: userUsername ?? '' });
  }, [form, userUsername]);

  return (
    <Card size="small" title="Profile">
      <Descriptions
        bordered
        size="small"
        column={1}
        items={[
          {
            key: 'username',
            label: 'Username',
            children: userUsername ?? EMPTY_VALUE,
          },
          {
            key: 'email',
            label: 'Email',
            children: userEmail ? (
              <Space size={SPACING.xs}>
                {userEmail}
                <CopyToClipboard text={userEmail} size="small" type="link" />
              </Space>
            ) : (
              EMPTY_VALUE
            ),
          },
          {
            key: 'uuid',
            label: 'UUID',
            children: userUuid ? (
              <Space size={SPACING.xs}>
                {userUuid}
                <CopyToClipboard text={userUuid} size="small" type="link" />
              </Space>
            ) : (
              EMPTY_VALUE
            ),
          },
        ]}
      />

      <Divider />

      <Form<IProfileForm>
        form={form}
        layout="vertical"
        onFinish={({ username }: IProfileForm): void => {
          dispatch(usernameChange(username.trim() || null));
        }}
      >
        <Form.Item
          name="username"
          label="Username"
          extra="Shown to other staff, e.g. in the order history. Leave empty to show your email instead."
          normalize={(value: string): string => value.toLowerCase()}
          rules={[
            {
              type: 'string',
              min: ADMIN_USERNAME_MIN_LENGTH,
              max: ADMIN_USERNAME_MAX_LENGTH,
            },
            {
              pattern: ADMIN_USERNAME_REGEX,
              message:
                'Use lowercase letters, digits, dots, dashes or underscores.',
            },
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            autoComplete="username"
            maxLength={ADMIN_USERNAME_MAX_LENGTH}
            disabled={userInProcess}
          />
        </Form.Item>

        <Space size={SPACING.lg} align="center">
          <Button type="primary" htmlType="submit" disabled={userInProcess}>
            Save profile
          </Button>
          <Spin size="small" spinning={userInProcess} />
        </Space>
      </Form>
    </Card>
  );
}

export default ProfileForm;
